import { FriendlyNameService } from '../../types/PanoramaArrowIdentifier';
/***
 * Represents a hotspot that is able to render or hide itself on virtual tour depending on current scene.
 */
export class AbstractAdaptiveHotspot {
    constructor(_virtualTourPosition, _id = FriendlyNameService.generateRandomId()) {
        this._virtualTourPosition = _virtualTourPosition;
        this._id = _id;
        this._virtualTour = null;
        // Virtual Tour Event unsubscriptions
        this._unsubscribeFromSceneChanges = null;
        this._unsubscribeFromVirtualTourDispose = null;
        /* Updates the scene on which the hotspot is placed. */
        this.updateVirtualTourPosition = (virtualTourPosition) => {
            const isCurrentlyRendered = this._virtualTourPosition ? this._isOnCurrentScene(this._virtualTourPosition) : false;
            const shouldBeRendered = this._isOnCurrentScene(virtualTourPosition);
            this._virtualTourPosition = virtualTourPosition;
            if (!this._virtualTour) {
                return;
            }
            if (isCurrentlyRendered && !shouldBeRendered) {
                this.hide(this._virtualTour);
            }
            if (!isCurrentlyRendered && shouldBeRendered) {
                this.render(this._virtualTour);
            }
        };
        /**
         * Attaches the hotspot to the virtual tour and renders it.
         */
        this.attach = (virtualTour) => {
            if (this._virtualTour) {
                throw Error('Hotspot is already attached to the virtual tour.');
            }
            this._virtualTour = virtualTour;
            this._unsubscribeFromSceneChanges = this._virtualTour.subscribeVirtualTourChanged(() => {
                if (!this._virtualTour) {
                    throw Error('Hotspot is not attached to virtual tour but listens to changes.');
                }
                if (this._isOnCurrentScene(this._virtualTourPosition)) {
                    this.render(this._virtualTour);
                }
                else {
                    this.hide(this._virtualTour);
                }
            });
            this._unsubscribeFromVirtualTourDispose = this._virtualTour.subscribeOnDispose(() => {
                this.detach();
            });
            if (this._isOnCurrentScene(this._virtualTourPosition)) {
                this.render(this._virtualTour);
            }
        };
        /* Detaches and hides hotspot from the virtual tour this was attached to. */
        this.detach = () => {
            var _a, _b;
            if (!this._virtualTour) {
                return;
            }
            this.detachCore();
            (_a = this._unsubscribeFromSceneChanges) === null || _a === void 0 ? void 0 : _a.call(this);
            (_b = this._unsubscribeFromVirtualTourDispose) === null || _b === void 0 ? void 0 : _b.call(this);
            this.hide(this._virtualTour);
            this._virtualTour = null;
        };
        /**
         * Returns true, if the position of the hotspot is the same as current scene in the virtual tour.
         */
        this._isOnCurrentScene = (virtualPosition) => {
            var _a, _b;
            const isOnScene = ((_a = this._virtualTour) === null || _a === void 0 ? void 0 : _a.getActiveTourSceneGuidView()) === virtualPosition.sceneGuid;
            const isOnSceneIndex = ((_b = this._virtualTour) === null || _b === void 0 ? void 0 : _b.getActiveSceneIndex()) === virtualPosition.sceneIndex;
            return isOnScene && isOnSceneIndex;
        };
    }
}
